import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Clock, Sparkles, ArrowRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { IMAGES } from "../data/demo";
import { StatusPill, Eyebrow, Demo } from "../components/Bits";

const imgFor = (k) => ({ property: IMAGES.business, cars: IMAGES.carNight, experiences: IMAGES.carLifestyle }[k] || IMAGES.business);

export default function Explore() {
  const { t } = useLang();
  const nav = useNavigate();
  const cats = t("explore.categories");
  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-14" data-testid="explore-page">
      <Eyebrow>{t("explore.eyebrow")}</Eyebrow>
      <h1 className="font-display text-4xl sm:text-5xl text-white mt-2">{t("explore.title")}</h1>
      <p className="text-white/60 mt-3 max-w-2xl">{t("explore.sub")}</p>
      <button onClick={() => nav("/ask")} data-testid="explore-ask-cta" className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-gold text-ink font-semibold hover:brightness-110 transition-all duration-200">
        {t("explore.ask_cta")} <ArrowRight size={15} />
      </button>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-10">
        {cats.map((c, i) => (
          <motion.button key={c.key} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.05 }}
            onClick={() => nav("/ask", { state: { prompt: `${t("explore.ask_cta")}: ${c.name}` } })} data-testid={`explore-card-${c.key}`}
            className={`text-left relative overflow-hidden rounded-2xl border border-white/[0.06] hover:border-gold/30 transition-all duration-300 group ${i === 0 ? "md:col-span-8 min-h-[240px]" : "md:col-span-4 min-h-[190px]"}`}>
            <img src={imgFor(c.key)} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-40 group-hover:scale-105 transition-all duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30" />
            <div className="relative p-6 h-full flex flex-col justify-end">
              <StatusPill status={c.status} />
              <div className="font-display text-2xl text-white mt-2">{c.name}</div>
              <div className="text-white/60 text-sm mt-1">{c.desc}</div>
            </div>
          </motion.button>
        ))}
      </div>

      <h2 className="font-display text-3xl text-white mt-16 mb-6">{t("explore.profile_h")}</h2>
      <div className="glass rounded-2xl p-7 max-w-2xl">
        <div className="flex items-center justify-between">
          <div><div className="font-display text-xl text-white">{t("explore.profile_name")}</div><div className="text-white/50 text-sm">{t("explore.profile_cat")}</div></div>
          <Demo />
        </div>
        <div className="flex flex-wrap gap-4 mt-4 text-sm text-white/70">
          <span className="flex items-center gap-1.5 text-emerald-400"><ShieldCheck size={14} /> {t("common.verified")}</span>
          <span className="flex items-center gap-1.5"><Clock size={14} /> {t("explore.profile_hours")}</span>
          <span className="text-gold">★ 4.8</span>
        </div>
        <div className="mt-4 p-4 rounded-xl bg-white/[0.03] border border-white/5">
          <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-wider mb-1"><Sparkles size={12} /> {t("common.why")}</div>
          <p className="text-white/60 text-sm">{t("explore.profile_why")}</p>
        </div>
      </div>
    </div>
  );
}
